import axios from "axios";
import { ApiSuccess, saUrl } from "./api_helper";
import type { PaginatedMeta } from "./audience_helper";
import { PLATFORM_TEMPLATE_GALLERY_TAGS } from "./platform_templates_helper";

export type PlatformTemplateDescription = {
  pt_BR?: string;
  en?: string;
  es?: string;
  [locale: string]: string | undefined;
};

export type PlatformTemplateCategory =
  | "newsletter"
  | "notice"
  | "promo"
  | "other";

export const PLATFORM_TEMPLATE_CATEGORIES: PlatformTemplateCategory[] = [
  "newsletter",
  "notice",
  "promo",
  "other",
];

export { PLATFORM_TEMPLATE_GALLERY_TAGS };

export type SaPlatformTemplate = {
  id: number;
  name: string;
  category: PlatformTemplateCategory | string;
  tags?: string[];
  variant_group?: string | null;
  variant_label?: string | null;
  description: PlatformTemplateDescription | null;
  html_body: string;
  editor_design: Record<string, unknown> | null;
  thumbnail_url?: string | null;
  is_active: boolean;
  sort_order: number;
  clone_count: number;
  season_starts_on?: string | null;
  season_ends_on?: string | null;
  season_status?: string | null;
  created_at?: string;
  updated_at?: string;
};

type ListResponse = ApiSuccess<SaPlatformTemplate[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

export type SaPlatformTemplatePayload = {
  name: string;
  category?: PlatformTemplateCategory | string;
  description?: PlatformTemplateDescription | null;
  html_body: string;
  editor_design?: Record<string, unknown> | null;
  is_active?: boolean;
  sort_order?: number;
  tags?: string[];
  variant_group?: string | null;
  variant_label?: string | null;
  season_starts_on?: string | null;
  season_ends_on?: string | null;
};

export type SaPlatformTemplatesListParams = {
  search?: string;
  is_active?: boolean;
  category?: string;
  page?: number;
  per_page?: number;
};

export type SaCloneStats = {
  days: number;
  total: number;
  by_day: { date: string; count: number }[];
  top: { id: number; name: string; count: number }[];
};

export const getSuperAdminPlatformTemplates = (
  params?: SaPlatformTemplatesListParams
): Promise<ListResponse> => {
  const query: Record<string, string | number | boolean> = {
    per_page: params?.per_page || 20,
  };

  if (params?.page) {
    query.page = params.page;
  }
  if (params?.search) {
    query.search = params.search;
  }
  if (params?.category) {
    query.category = params.category;
  }
  if (typeof params?.is_active === "boolean") {
    query.is_active = params.is_active ? 1 : 0;
  }

  return axios.get(saUrl("/super-admin/platform-templates"), { params: query });
};

export const getSuperAdminPlatformTemplate = (
  id: number
): Promise<ApiSuccess<SaPlatformTemplate>> => {
  return axios.get(saUrl(`/super-admin/platform-templates/${id}`));
};

export const createSuperAdminPlatformTemplate = (
  payload: SaPlatformTemplatePayload
): Promise<ApiSuccess<SaPlatformTemplate>> => {
  return axios.post(saUrl("/super-admin/platform-templates"), payload);
};

export const updateSuperAdminPlatformTemplate = (
  id: number,
  payload: Partial<SaPlatformTemplatePayload>
): Promise<ApiSuccess<SaPlatformTemplate>> => {
  return axios.patch(saUrl(`/super-admin/platform-templates/${id}`), payload);
};

export const deleteSuperAdminPlatformTemplate = (
  id: number
): Promise<ApiSuccess<null>> => {
  return axios.delete(saUrl(`/super-admin/platform-templates/${id}`));
};

export const reorderSuperAdminPlatformTemplates = (
  orderedIds: number[]
): Promise<ApiSuccess<null>> => {
  return axios.post(saUrl("/super-admin/platform-templates/reorder"), {
    ordered_ids: orderedIds,
  });
};

export const getSuperAdminPlatformTemplateCloneStats = (
  days = 14
): Promise<ApiSuccess<SaCloneStats>> => {
  return axios.get(saUrl("/super-admin/platform-templates/clone-stats"), {
    params: { days },
  });
};

export const importSuperAdminPlatformTemplateHtml = (
  url: string
): Promise<ApiSuccess<{ html_body: string }>> => {
  return axios.post(saUrl("/super-admin/platform-templates/import-html"), {
    url,
  });
};

export const uploadSuperAdminPlatformTemplateThumbnail = (
  id: number,
  file: File
): Promise<ApiSuccess<{ thumbnail_url: string; path: string }>> => {
  const body = new FormData();
  body.append("image", file);
  return axios.post(
    saUrl(`/super-admin/platform-templates/${id}/thumbnail`),
    body,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

export const deleteSuperAdminPlatformTemplateThumbnail = (
  id: number
): Promise<ApiSuccess<{ thumbnail_url: null }>> => {
  return axios.delete(saUrl(`/super-admin/platform-templates/${id}/thumbnail`));
};

export const regenerateSuperAdminPlatformTemplateThumbnail = (
  id: number,
  force = true
): Promise<
  ApiSuccess<{
    thumbnail_url: string | null;
    thumbnail_source?: string | null;
    skipped?: boolean;
  }>
> => {
  return axios.post(
    saUrl(`/super-admin/platform-templates/${id}/thumbnail/regenerate`),
    { force }
  );
};

export type SaSeasonCalendarPack = {
  key: string;
  tag: string;
  default_starts_on: string | null;
  default_ends_on: string | null;
  templates: {
    id: number;
    name: string;
    is_active: boolean;
    season_starts_on: string | null;
    season_ends_on: string | null;
    season_status: string | null;
  }[];
};

export type SaSeasonCalendar = {
  year: number;
  packs: SaSeasonCalendarPack[];
};

export const getSuperAdminPlatformTemplateSeasonCalendar = (
  year?: number
): Promise<ApiSuccess<SaSeasonCalendar>> => {
  return axios.get(saUrl("/super-admin/platform-templates/season-calendar"), {
    params: year ? { year } : undefined,
  });
};

export const applySuperAdminPlatformTemplateSeason = (payload: {
  pack: string;
  year: number;
  clear?: boolean;
  starts_on?: string | null;
  ends_on?: string | null;
}): Promise<ApiSuccess<{ updated: number; pack: string; year: number }>> => {
  return axios.post(
    saUrl("/super-admin/platform-templates/season-apply"),
    payload
  );
};
